import React, { createContext, useState, ReactNode, useEffect, useMemo } from 'react';
import * as api from '~/services/parallel';
import { IIntegrationMapping } from '~/services/parallel/integrations.types';
import { useSelector } from 'react-redux';
import { State } from '~/store';
import { IContextTypeEnum } from './useFormulaContext';
import objectHash from 'object-hash';
import { IRecordTypeEnum } from '../../components/FormulasTable/TableBody';
import { IFormula, IRecipe, IUpdateSortOrderParams } from '~/services/parallel/formulas.types';
import { DragEndEvent } from '@dnd-kit/core';
import { cloneDeep } from 'lodash';
import { createMonthArrayBetweenDates } from '~/utils/dates/createMonthArrayBetweenDates';
import { toZonedTime } from 'date-fns-tz';
import { format } from 'date-fns';

enum IFormattingEnum {
  Number = 'number',
  Currency = 'currency',
  Percent = 'percent',
}

enum IRoundDirectionEnum {
  Up = 'up',
  Down = 'down',
  Nearest = 'nearest',
}

export interface IRoundingInstructions {
  direction: IRoundDirectionEnum;
  precision: number;
}

type IMonthlyValues = Record<
  string,
  {
    calculatedValue: number;
    overrideValue: number | null;
    actualValue: number | null;
    formatting: IFormattingEnum | null;
    roundingInstructions: IRoundingInstructions | null;
  }
>;
export interface IFormulaItemData {
  type: IRecordTypeEnum.Formula;
  formulaUuid: string;
  label: {
    name: string;
    integration: string | null;
    isIsolated: boolean;
  };
  formula: {
    recipe: IRecipe;
  };
  monthlyValues: IMonthlyValues;
}

export interface IFormulaGroupData {
  type: IRecordTypeEnum.Group;
  uuid: string;
  name: string;
  isCollapsed: boolean;
  formulas: IFormulaItemData[];
}

export interface IModelBuilderContext {
  type: IContextTypeEnum;
  formulasData: IFormulaGroupData[];
  toggleGroupCollapse: (value: string | boolean) => void;
  onDragEnd: (value: DragEndEvent) => void;
  updateSortOrder: (updatedFormulasData: IFormulaGroupData[]) => Promise<void>;
  formulaDictionary: Record<string, IFormula>;
  selectedMonths: string[];
}

export const ModelBuilderContext = createContext<IModelBuilderContext | undefined>(undefined);

interface ModelBuilderProviderProps {
  children: ReactNode;
}

export const ModelBuilderProvider: React.FC<ModelBuilderProviderProps> = ({ children }) => {
  const { organization, scenario, user } = useSelector((state: State) => state);
  const { defaultGraphEndDate, defaultGraphStartDate } = user.preferences;
  const [formulasData, setFormulasData] = useState<IFormulaGroupData[]>([]);
  const [formulaDictionary, setFormulaDictionary] = useState<Record<string, IFormula>>({});

  const selectedMonths = useMemo(() => {
    return createMonthArrayBetweenDates(
      toZonedTime(user.preferences.defaultGraphStartDate, 'UTC'),
      toZonedTime(user.preferences.defaultGraphEndDate, 'UTC'),
    ).map((month) => format(month, 'MMM yyyy'));
  }, [user.preferences.defaultGraphEndDate, user.preferences.defaultGraphStartDate]);

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      const formulasList = await api.formulas.list({
        organizationUuid: organization.uuid,
        startDate: new Date(defaultGraphStartDate),
        endDate: new Date(defaultGraphEndDate),
        scenarioUuid: scenario.activeScenarioData?.uuid ?? undefined,
      });

      const formulasDictionary = formulasList.reduce((output: Record<string, IFormula>, formula) => {
        output[formula.formulaUuid] = formula;
        return output;
      }, {});
      setFormulaDictionary(formulasDictionary);

      const sortOrder = await api.formulas.getSortOrder({
        organizationUuid: organization.uuid,
      });

      const integrationsList = await api.integrations.list({
        organizationUuid: organization.uuid,
      });

      let integrationMappings: IIntegrationMapping[] = [];
      if (integrationsList.length) {
        integrationMappings = await api.integrations.listMappings({
          organizationUuid: organization.uuid,
          integrationUuid: integrationsList[0].uuid,
          scope: 'modelBuilder',
        });
      }

      const integrationMappingsDictionary = integrationMappings.reduce(
        (output: Record<string, string>, integrationMapping) => {
          if (integrationMapping.uuid in output) return output;
          const integrationSlug = integrationsList.find(
            (integration) => integration.uuid === integrationMapping.integrationUuid,
          );
          if (integrationSlug) {
            output[integrationMapping.uuid] = integrationSlug.source?.slug ?? '';
          }
          return output;
        },
        {},
      );

      const tableData = sortOrder.groups.map((group): IFormulaGroupData => {
        const formulas = group.sortOrder.map((formulaUuid): IFormulaItemData => {
          const isReferencedElsewhere = formulasList.some((f) =>
            Object.values(f.recipe.variables).some((variable) => variable.formulaUuid === formulaUuid),
          );
          const formula = formulasDictionary[formulaUuid];

          const actualsDict = formula.actuals.reduce<Record<string, number>>((output, actual) => {
            output[format(actual.date, 'MMM yyyy')] = actual.value;
            return output;
          }, {});

          const overridesDict = formula.overrides.reduce<Record<string, number>>((output, override) => {
            output[format(override.date, 'MMM yyyy')] = override.value;
            return output;
          }, {});

          const calculationsDict = formula.calculations.reduce<Record<string, number>>((output, calculation) => {
            output[format(calculation.date, 'MMM yyyy')] = calculation.value ?? 0;
            return output;
          }, {});

          const monthlyValues = selectedMonths.reduce<IMonthlyValues>((output, month) => {
            output[month] = {
              calculatedValue: calculationsDict[month] ?? 0,
              overrideValue: overridesDict[month] ?? null,
              actualValue: actualsDict[month] ?? null,
              formatting: formula.formatting ?? null,
              roundingInstructions: formula.recipe.roundingInstructions ?? null,
            };
            return output;
          }, {});

          return {
            type: IRecordTypeEnum.Formula,
            formulaUuid: formula.formulaUuid,
            label: {
              name: formula.recipe.name,
              integration:
                (formula.dataSourceUuids.length && integrationMappingsDictionary[formula.dataSourceUuids[0]]) || null,
              isIsolated: !isReferencedElsewhere,
            },
            formula: {
              recipe: formula.recipe,
            },
            monthlyValues,
          };
        });

        return {
          type: IRecordTypeEnum.Group,
          uuid: objectHash(group),
          name: group.name,
          isCollapsed: false,
          formulas,
        };
      });

      setFormulasData(tableData);
    };
    if (organization.uuid) {
      fetchData();
    }
  }, []);

  const toggleGroupCollapse = (value: string | boolean): void => {
    if (typeof value === 'string') {
      // Toggle individual group
      setFormulasData((prev) =>
        prev.map((group) => (group.uuid === value ? { ...group, isCollapsed: !group.isCollapsed } : group)),
      );
    } else {
      // Toggle all groups at once
      setFormulasData((prev) => prev.map((group) => ({ ...group, isCollapsed: value })));
    }
  };

  const onDragEnd = (value: DragEndEvent): void => {
    const { active, over } = value;
    if (!over) return;

    const clonedGroups = cloneDeep(formulasData);
    // Find the group and formula of the active item
    const sourceGroup = clonedGroups.find((group) =>
      group.formulas.some((formula) => formula.formulaUuid === active.id.toString()),
    );

    const targetGroup = clonedGroups.find(
      (group) =>
        group.formulas.some((formula) => formula.formulaUuid === over.id.toString()) ||
        group.uuid === over.id.toString(),
    );

    if (sourceGroup && targetGroup) {
      const sourceIndex = sourceGroup.formulas.findIndex((f) => f.formulaUuid === active.id.toString());
      const targetIndex = targetGroup.formulas.findIndex((f) => f.formulaUuid === over.id.toString());

      // If moving within the same group, reorder
      if (sourceGroup.name === targetGroup.name) {
        const [movedFormula] = sourceGroup.formulas.splice(sourceIndex, 1);

        if (sourceIndex > targetIndex) {
          // Moving down the list
          targetGroup.formulas.splice(targetIndex + 1, 0, movedFormula);
        } else {
          // Moving up the list
          targetGroup.formulas.splice(targetIndex, 0, movedFormula);
        }
      } else {
        // Remove the item from its original position
        const [movedFormula] = sourceGroup.formulas.splice(
          sourceGroup.formulas.findIndex((f) => f.formulaUuid === active.id.toString()),
          1,
        );
        if (targetGroup.isCollapsed) {
          // If moving to a closed group, add it to the beginning of the target group
          targetGroup.formulas.unshift(movedFormula);
        } else {
          // Add to after the target index
          targetGroup.formulas.splice(targetIndex + 1, 0, movedFormula);
        }
      }
    }

    updateSortOrder(clonedGroups);
  };

  const updateSortOrder = async (updatedFormulasData: IFormulaGroupData[]): Promise<void> => {
    setFormulasData(updatedFormulasData);

    const sortOrder: IUpdateSortOrderParams = {
      organizationUuid: organization.uuid,
      scenarioUuid: scenario.activeScenarioData?.uuid ?? undefined,
      groups: updatedFormulasData.map((group) => ({
        name: group.name,
        sortOrder: group.formulas.map((formula) => formula.formulaUuid),
      })),
    };

    await api.formulas.updateSortOrder(sortOrder);
  };

  const value = {
    type: IContextTypeEnum.ModelBuilder,
    formulasData,
    formulaDictionary,
    toggleGroupCollapse,
    onDragEnd,
    updateSortOrder,
    selectedMonths,
  };

  return <ModelBuilderContext.Provider value={value}>{children}</ModelBuilderContext.Provider>;
};
